import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatsCard } from '../components/StatsCard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SkeletonStats, SkeletonCard } from '../components/ui/skeleton';
import { certificatesAPI, logsAPI } from '../lib/api';
import { calculateDaysRemaining, getCertificateStatus } from '../lib/utils';
import { STATUS } from '../lib/theme';
import { Shield, AlertTriangle, CheckCircle, Clock, Activity, ArrowRight } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { id } from 'date-fns/locale';

// WITA Timezone (Waktu Indonesia Tengah - UTC+8)
const WITA_TIMEZONE = 'Asia/Makassar';

export function Overview() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [certificates, setCertificates] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);


  useEffect(() => {
    loadData();
    // Auto refresh setiap 60 detik
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      // Prepare logs parameters with date filters
      const logsParams = {
        page: 1,
        per_page: 5
      };


      const [certsResponse, logsResponse] = await Promise.all([
        certificatesAPI.getAll(),
        logsAPI.getLogs(logsParams)
      ]);

      setCertificates(certsResponse.data);

      // Handle different API response structures for logs
      let logs = [];
      if (logsResponse.data) {
        if (Array.isArray(logsResponse.data)) {
          logs = logsResponse.data;
        } else if (logsResponse.data.logs && Array.isArray(logsResponse.data.logs)) {
          logs = logsResponse.data.logs;
        } else if (logsResponse.data.data && Array.isArray(logsResponse.data.data)) {
          logs = logsResponse.data.data;
        }
      }

      setRecentLogs(logs);
    } catch (error) {
      toast.error('Gagal memuat data');
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleViewAllLogs = () => {
    navigate('/logs');
  };

  /**
   * Format timestamp dengan timezone WITA
   * @param {string|Date} timestamp - Timestamp dari API
   * @returns {string} Formatted date string in WITA
   */
  const formatTimestampWITA = (timestamp) => {
    try {
      if (!timestamp) return '-';

      // Parse timestamp ke Date object
      const date = new Date(timestamp);

      // Convert ke WITA timezone using toZonedTime
      const witaDate = toZonedTime(date, WITA_TIMEZONE);

      // Format: "03 Des 2025, 14:30 WITA"
      return format(witaDate, 'dd MMM yyyy, HH:mm', { locale: id }) + ' WITA';
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return timestamp;
    }
  };

  /**
   * Format date range display dengan WITA
   */
  const formatDateRangeWITA = (start, end) => {
    try {
      const startDate = toZonedTime(new Date(start), WITA_TIMEZONE);
      const endDate = toZonedTime(new Date(end), WITA_TIMEZONE);

      return `${format(startDate, 'dd MMM', { locale: id })} - ${format(endDate, 'dd MMM yyyy', { locale: id })}`;
    } catch (error) {
      return `${start} - ${end}`;
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div>
          <div className="h-5 w-48 bg-muted animate-pulse rounded-sm mb-2" />
          <div className="h-3 w-72 bg-muted animate-pulse rounded-sm" />
        </div>
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <SkeletonStats />
          <SkeletonStats />
          <SkeletonStats />
          <SkeletonStats />
        </div>
        <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    );
  }

  // Hitung statistik
  const totalCerts = certificates.length;
  const validCerts = certificates.filter(cert => {
    const days = calculateDaysRemaining(cert.expiration_date);
    return getCertificateStatus(days) === 'valid';
  }).length;

  const criticalCerts = certificates.filter(cert => {
    const days = calculateDaysRemaining(cert.expiration_date);
    return getCertificateStatus(days) === 'critical';
  }).length;

  const warningCerts = certificates.filter(cert => {
    const days = calculateDaysRemaining(cert.expiration_date);
    return getCertificateStatus(days) === 'warning';
  }).length;

  const expiringSoon = warningCerts + criticalCerts;

  const expired = certificates.filter(cert => {
    const days = calculateDaysRemaining(cert.expiration_date);
    return getCertificateStatus(days) === 'expired';
  }).length;

  // Generate mock trend data untuk sparklines (dalam real app, ini dari API)
  const generateTrendData = (value) => {
    return Array.from({ length: 7 }, (_, i) => ({
      value: Math.max(0, value + Math.floor(Math.random() * 3) - 1)
    }));
  };

  return (
    <div className="space-y-4">
      {/* Breadcrumb + Page Header */}
      <div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
          <span>Manajemen</span>
          <span>›</span>
          <span style={{ color: STATUS.primary.text }} className="font-medium">Ringkasan Dashboard</span>
        </div>
        <h2 className="text-base sm:text-lg font-semibold tracking-tight">Ringkasan Dashboard</h2>
        <p className="text-muted-foreground mt-0.5 text-xs">
          Monitor status sertifikat SSL untuk semua domain
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          title="Total Sertifikat"
          value={totalCerts}
          description="Jumlah total sertifikat"
          icon={Shield}
          status="primary"
          trend={0}
          trendData={generateTrendData(totalCerts)}
        />
        <StatsCard
          title="Sertifikat Valid"
          value={validCerts}
          description="Sertifikat masih valid"
          icon={CheckCircle}
          status="valid"
          trend={2}
          trendData={generateTrendData(validCerts)}
        />
        <StatsCard
          title="Akan Kedaluwarsa"
          value={expiringSoon}
          description="Akan expire dalam 30 hari"
          icon={Clock}
          status="warning"
          trend={expiringSoon > 0 ? -1 : 0}
          trendData={generateTrendData(expiringSoon)}
        />
        <StatsCard
          title="Kedaluwarsa"
          value={expired}
          description="Sertifikat sudah kedaluwarsa"
          icon={AlertTriangle}
          status="critical"
          trend={expired > 0 ? -1 : 0}
          trendData={generateTrendData(expired)}
        />
      </div>

      <div className="grid gap-4 grid-cols-1 lg:grid-cols-2 lg:items-stretch">
        {/* Certificates Expiring Soon */}
        <Card className="flex flex-col w-full min-h-[400px]">
          <CardHeader className="flex-shrink-0">
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" style={{ color: STATUS.warning.icon }} />
              Sertifikat Akan Kedaluwarsa
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            <div className="space-y-2">
              {certificates
                .filter(cert => {
                  const days = calculateDaysRemaining(cert.expiration_date);
                  const status = getCertificateStatus(days);
                  return status === 'warning' || status === 'critical';
                })
                .slice(0, 5)
                .map((cert) => {
                  const days = calculateDaysRemaining(cert.expiration_date);
                  const colors = days <= 7 ? STATUS.critical : STATUS.warning;
                  return (
                    <div
                      key={cert.id}
                      className="flex items-center justify-between p-3 border rounded-sm hover:bg-accent/50 transition-colors gap-4"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate text-xs sm:text-sm">{cert.domain}</p>
                        <p className="text-xs text-muted-foreground truncate">{cert.server_source}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                        <Badge
                          className="whitespace-nowrap"
                          style={{ background: colors.bg, borderColor: colors.border, color: colors.text }}
                        >
                          {days} hari
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              {expiringSoon === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2" style={{ color: STATUS.valid.icon }} />
                  <p className="text-sm">Semua sertifikat dalam kondisi baik</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity with Date Filter - WITH WITA TIMEZONE */}
        <Card className="flex flex-col w-full min-h-[400px]">
          <CardHeader className="flex-shrink-0">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-4 w-4 flex-shrink-0" style={{ color: STATUS.primary.icon }} />
                  Aktivitas Terkini
                </CardTitle>
                <CardDescription className="mt-1">
                  Menampilkan 5 aktivitas terbaru
                </CardDescription>
              </div>

              {/* View All Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleViewAllLogs}
                className="gap-1.5 whitespace-nowrap flex-shrink-0"
              >
                <span>Lihat Semua</span>
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            <div className="space-y-2">
              {recentLogs.length > 0 ? (
                recentLogs.slice(0, 5).map((log, index) => {
                  const dotColor = log.level === 'ERROR' || log.level === 'CRITICAL'
                    ? STATUS.critical.icon
                    : log.level === 'WARNING'
                      ? STATUS.warning.icon
                      : STATUS.primary.icon;
                  return (
                    <div
                      key={log.id || index}
                      className="flex items-start gap-3 p-3 border rounded-sm hover:bg-accent/50 transition-colors"
                    >
                      <div
                        className="mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0"
                        style={{ background: dotColor }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium break-words">
                          {log.message || log.action || log.description || 'Aktivitas tidak diketahui'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatTimestampWITA(log.timestamp || log.created_at || log.date)}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Activity className="h-8 w-8 mx-auto mb-2" />
                  <p className="text-sm">Belum ada aktivitas</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}